import { useState } from "react";

import { useCurrentWorkspace } from "@accurx/auth";
import { Ds, Feedback } from "@accurx/design";
import { NetworkError } from "@accurx/shared";
import { useIsMutating } from "@tanstack/react-query";
import { useDirectory } from "domains/directory/context/DirectoryProvider";
import {
    ContactCreationError,
    useCreateContact,
} from "domains/directory/hooks/mutations/useCreateContactMutation";
import { EmailValidationKey } from "domains/directory/hooks/mutations/useEmailValidationMutation";
import {
    ContactFormFieldErrors,
    ContactFormSubmission,
} from "domains/directory/schemas/contact-form/ContactFormSchema";
import { toast } from "react-toastify";
import styled from "styled-components";

import { DirectoryContactForm } from "../DirectoryContactForm/DirectoryContactForm";
import { DirectoryModalFooter } from "../DirectoryModal/DirectoryModalFooter";
import { EmailValidation } from "./EmailValidation/EmailValidation";

export const DirectoryAddContact = () => {
    const { orgId: workspaceId } = useCurrentWorkspace();

    const isValidatingEmail =
        useIsMutating({ mutationKey: [EmailValidationKey] }) > 0;

    const { setView, setSelectedContactId } = useDirectory();

    const [email, setEmail] = useState("");
    const [showDiscardConfirmation, setShowDiscardConfirmation] =
        useState(false);
    const [errors, setErrors] = useState<ContactFormFieldErrors>();

    const { mutate: createContact, isLoading: isAddingContact } =
        useCreateContact({
            onSuccess: (data) => {
                setSelectedContactId(data.contact.id ?? undefined);
                setView("ContactDetails");
                toast(
                    <Feedback
                        colour="success"
                        title="Contact created successfully"
                    />,
                );
            },
            onError: (error) => {
                if (error instanceof ContactCreationError) {
                    setErrors(error.fieldErrors);
                    return;
                }
                toast(
                    <Feedback
                        colour="error"
                        title={
                            error instanceof NetworkError
                                ? "Couldn’t create contact. Please check your connection and try again."
                                : "Couldn’t create contact. Please try again."
                        }
                    />,
                );
            },
        });

    const handleSubmit = (data: ContactFormSubmission) => {
        createContact({
            workspaceId,
            ...data,
        });
    };

    return (
        <>
            <DirectoryPageMainContent>
                <DirectoryPageContainer mt="3">
                    <Ds.Text
                        mb="1"
                        as="h1"
                        size="large"
                        weight="bold"
                        color="night"
                    >
                        Add contact
                    </Ds.Text>
                    <Ds.Text mb="2" color="night">
                        Contact will only be visible to your workspace
                    </Ds.Text>
                    {email ? (
                        <DirectoryContactForm
                            id="add-contact"
                            emailAddress={email}
                            errors={errors}
                            onSubmit={handleSubmit}
                            onError={setErrors}
                        />
                    ) : (
                        <EmailValidation onValid={setEmail} />
                    )}
                </DirectoryPageContainer>
            </DirectoryPageMainContent>
            <DirectoryModalFooter>
                <DirectoryPageContainer>
                    <Ds.Flex justifyContent="space-between">
                        <Ds.Button
                            type="button"
                            appearance="tertiary"
                            onClick={() =>
                                email
                                    ? setShowDiscardConfirmation(true)
                                    : setView("Search")
                            }
                        >
                            Cancel
                        </Ds.Button>
                        <Ds.Button
                            type="submit"
                            disabled={isValidatingEmail || isAddingContact}
                            form="add-contact"
                        >
                            {email ? "Save" : "Continue"}
                        </Ds.Button>
                    </Ds.Flex>
                </DirectoryPageContainer>
            </DirectoryModalFooter>
            <Ds.Modal
                open={showDiscardConfirmation}
                onOpenChange={setShowDiscardConfirmation}
            >
                <Ds.Modal.Section>
                    <Ds.Flex flexDirection="column" gap="2">
                        <Ds.Modal.Title>
                            Discard unsaved changes?
                        </Ds.Modal.Title>
                        <Ds.Flex gap="2" justifyContent="flex-end">
                            <Ds.Button
                                appearance="secondary"
                                onClick={() =>
                                    setShowDiscardConfirmation(false)
                                }
                            >
                                Cancel
                            </Ds.Button>
                            <Ds.Button onClick={() => setView("Search")}>
                                Discard
                            </Ds.Button>
                        </Ds.Flex>
                    </Ds.Flex>
                </Ds.Modal.Section>
            </Ds.Modal>
        </>
    );
};

const DirectoryPageContainer = styled(Ds.Box).attrs({
    mx: { xs: "2", lg: "30%" },
})``;

const DirectoryPageMainContent = styled(Ds.Flex.Item).attrs({
    flex: "1 1 0",
})`
    overflow: auto;
    padding-bottom: 1rem;
`;
